// types.ts
export interface Point {
  x: number;
  y: number;
}

// 色の定義
export interface PouchColor {
  id: string;
  value: string;
  name: string;
}

export const POUCH_COLORS: PouchColor[] = [
  { id: 'gray', value: '#adb5bd', name: 'Gray' },
  { id: 'orange', value: '#ff922b', name: 'Orange' },
  { id: 'yellow-green', value: '#94d82d', name: 'Yellow Green' },
  { id: 'sky-blue', value: '#4dabf7', name: 'Sky Blue' },
  { id: 'red-purple', value: '#da77f2', name: 'Red Purple' },
  { id: 'pink', value: '#faa2c1', name: 'Pink' }
];

// グループの色定義
export interface GroupColor {
  id: string;
  value: string;
  name: string;
}

export const GROUP_COLORS: GroupColor[] = [
  { id: 'yellow', name: 'Yellow', value: '#ffd700' },
  { id: 'green', name: 'Green', value: '#4caf50' },
  { id: 'blue', name: 'Blue', value: '#2196f3' },
  { id: 'purple', name: 'Purple', value: '#9c27b0' },
  { id: 'red', name: 'Red', value: '#f44336' },
  { id: 'gray', name: 'Gray', value: '#808080' }
];

// 履歴アクションタイプ
export enum HistoryActionType {
  INIT = 'Initial State',
  ADD_POUCH = 'Add Pouch',
  EDIT_POUCH_VALUE = 'Edit Pouch Value',
  EDIT_GTP_VALUE = 'Edit GTP Value',
  EDIT_POUCH_TITLE = 'Edit Pouch Title',
  EDIT_GROUP_TITLE = 'Edit Group Title',
  MOVE_POUCH = 'Move Pouch',
  DELETE_POUCH = 'Delete Pouch',
  ADD_GROUP = 'Add Group',
  MOVE_GROUP = 'Move Group',
  RESIZE_GROUP = 'Resize Group',
  DELETE_GROUP = 'Delete Group',
  CHANGE_GROUP_COLOR = 'Change Group Color',
  MERGE_GROUP = 'Merge Group',
  CHANGE_POUCH_COLOR = 'Change Pouch Color',
  RESTORE_SNAPSHOT = 'Restore Snapshot',
  CLONE_POUCH = 'Clone Pouch',
  CLONE_GROUP = 'Clone Group',
  MERGE_POUCH = 'Merge Pouch',
  PASTE_BULK_UPDATE = 'Paste Bulk Update',
  TOGGLE_BALANCING_POUCH = 'Toggle Balancing Pouch'
}

// パウチの型定義
export interface Pouch {
  id: string;
  value: number;
  title?: string;
  multiplier: number;
  x: number;
  y: number;
  isNegative: boolean;
  zIndex?: number;
  color: string;
  
  // グループトータルパウチ用プロパティ
  isGroupTotalPouch?: boolean; // グループトータルパウチかどうか
  referencedGroupId?: string; // 参照しているグループのID
  oppositeToTotal?: boolean; // グループトータルと逆の正負で表示するかどうか
  
  // 数式記録用プロパティ
  lastExpression?: string; // 最後に入力された数式
  valueWhenLastExpression?: number; // 数式記録時の値
  multiplyWhenLastExpression?: number; // 数式記録時のマルチプライヤー
  isTime?: boolean; // 時間として認識されているかどうか
  
  // UIとアニメーション用フラグ (実際のデータには保存しない)
  _animateFromZero?: boolean;
  _animateSignChange?: boolean;
  _animateMultiplier?: boolean; // 乗数変更時のアニメーション用フラグ
  _noTransition?: boolean;
  _isDeleting?: boolean;
  _bounceBack?: boolean; // アニメーション用フラグ（バウンスエフェクト）
}

// グループの型定義
export interface Group {
  id: string;
  path: string;
  pouches: string[];
  color?: string;
  originalColor?: string;    // ミュート前の元の色を記録
  title?: string;
  placeholderText?: string;  // プレースホルダーとして表示する名前
  zIndex?: number;
  _isCloning?: boolean;      // 複製中のUI用フラグ
  _isDragging?: boolean;     // ドラッグ中のフラグ
  _isFromOtherSheet?: boolean; // シート間ドラッグ用のフラグ
  _animateSubtotal?: boolean; // サブトータル変更時のアニメーション用フラグ
  _previousSubtotal?: number; // 前回のサブトータル保存用（アニメーション用）
  _isInMuteGroup?: boolean; // ミュートグループに内包されているかのフラグ
  
  // 内包関係は他のグループのpathプロパティで管理
  
  // UIとアニメーション用
  _animateFromZero?: boolean; // アニメーションフラグ
  _isAdding?: boolean; // 追加アニメーション用フラグ
  _isDeleting?: boolean; // 削除アニメーション用フラグ
}

// 凡例の型定義
export interface PouchColorLegend {
  colorId: string;
  legend: string;
  _animateSubtotal?: boolean;
}

// 履歴エントリの型定義
export interface SheetHistoryEntry {
  timestamp: Date;
  pouches: Pouch[];
  groups: Group[];
  description: string;
  type: HistoryActionType;
  targetId?: string; // 操作対象のPouchまたはGroupのID
  /** true のときだけ、次の値確定(EDIT)を履歴に積まず ADD エントリを上書きする（新規パウチの初回のみ） */
  addPouchAwaitingFirstValue?: boolean;
  balancingPouchId?: string | null;
  balancingTargetTotal?: number;
}

// スナップショットの型定義
export interface SheetSnapshot {
  id: string;
  timestamp: Date;
  pouches: Pouch[];
  groups: Group[];
  name: string;
  description: string;
}

// シートの型定義
export interface Sheet {
  id: string;
  title?: string;
  pouches: Pouch[];
  groups: Group[];
  createdAt: Date;
  lastModified?: Date;
  colorLegends?: PouchColorLegend[];
  lastPouchValue?: number;
  lastPouchColor?: string;
  lastGroupColor?: string;
  
  // 履歴管理用
  history: SheetHistoryEntry[];
  historyIndex: number;
  snapshots: SheetSnapshot[];
  
  // 帳尻パウチ（Balancing Pouch）
  balancingPouchId?: string | null;
  balancingTargetTotal?: number;
}

// 設定の型定義
export interface Settings {
  buttonsVisible: boolean;
  posinegaVisible: boolean;
  defaultValue: number;
  autoHideMenu: boolean;
  isLeftSidebarOpen: boolean;
  isRightSidebarOpen: boolean;
}

// 統計情報の型定義
export interface Statistics {
  count: number;
  total: number;
  average: number;
  max: number;
  min: number;
}

// アプリ全体の状態
export interface AppState {
  sheets: Sheet[];
  activeSheetId: string;
  settings: Settings;
  isMobile: boolean;
  isTouchDevice: boolean;
  isLeftSidebarOpen: boolean;
  isRightSidebarOpen: boolean;
  isRightSidebarFullyClosed: boolean; // サイドバーが完全に閉じたかどうか
}

// アクションの定義
export type AppAction =
  | { type: 'INIT_APP', payload: { sheets: Sheet[], settings: Settings } }
  | { type: 'SET_ACTIVE_SHEET', payload: string }
  | { type: 'ADD_SHEET', payload: Sheet }
  | { type: 'UPDATE_SHEET', payload: Sheet }
  | { type: 'DELETE_SHEET', payload: string }
  | { type: 'ADD_POUCH', payload: Pouch }
  | { type: 'UPDATE_POUCH', payload: Pouch }
  | { type: 'DELETE_POUCH', payload: string }
  | { type: 'ADD_GROUP', payload: Group }
  | { type: 'UPDATE_GROUP', payload: Group }
  | { type: 'DELETE_GROUP', payload: string }
  | { type: 'UPDATE_SETTINGS', payload: Settings }
  | { type: 'TOGGLE_MENU', payload?: boolean }
  | { type: 'TOGGLE_SIDEBAR', payload?: boolean }
  | { type: 'SET_RIGHT_SIDEBAR_FULLY_CLOSED', payload: boolean }
  | { type: 'SET_MOBILE_STATE', payload: boolean }
  | { type: 'SET_ACTIVE_SHEET_POUCHES', payload: Pouch[] }
  | { type: 'SET_ACTIVE_SHEET_GROUPS', payload: Group[] };

// アクティブなシートの作業データ
export interface ActiveSheetState {
  pouches: Pouch[];
  groups: Group[];
  tempValues: Record<string, number>;  // 編集中のパウチの一時的な値
  hoveredPouchId: string | null;
  hoveredGroupId: string | null;
  draggingPouchId: string | null;
  draggingGroupId: string | null;
  activeGroupId: string | null;
  editingGroupId: string | null;
  maxZIndex: number;
  maxGroupZIndex: number;
  isDrawingGroup: boolean;
  pathPoints: Point[];
  currentPath: string;
  statistics: Statistics;
}

// UI関連の状態
export interface UIState {
  isMobile: boolean;
  isTouchDevice: boolean;
  isLeftSidebarOpen: boolean;
  isRightSidebarOpen: boolean;
  isSheetSwitching: boolean;
  isDragging: boolean;
  dragEndTimestamp: number;
  deleteTimestamp: number;
  pendingPouch: {
    x: number;
    y: number;
    color: string;
    timeoutId: number | null;
  } | null;
} 